import { isMock, db, storage } from './firebase'
import { logger } from './logger'
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  serverTimestamp,
  DocumentData,
  Timestamp
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, type UploadResult } from 'firebase/storage'
import { optimizeImage } from './imageOptimizer'
import type { Banner, BannerFilters } from '../types/banner'

// Firestore collections
const BANNERS_COLLECTION = 'banners'
const IMAGES_PATH = 'banner-images'

// Helper function to convert Firestore data to Banner
const firestoreToBanner = (docId: string, data: DocumentData): Banner => {
  // Convert Firestore Timestamp to Date
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()

  // Normalize position values to match allowed positions
  const normalizePosition = (rawPosition: string | undefined): Banner['position'] => {
    const positionMap: Record<string, Banner['position']> = {
      'header': 'header-top',
      'sidebar': 'sidebar-right',
      'left': 'sidebar-left',
      'right': 'sidebar-right',
      'top': 'header-top',
      'bottom': 'footer-bottom',
      'middle': 'between-sections',
      'center': 'between-sections',
      'modal': 'popup',
      'pop': 'popup',
    }
    const allowedPositions: Banner['position'][] = ['sidebar-left', 'sidebar-right', 'header-top', 'footer-bottom', 'between-sections', 'popup']
    
    if (!rawPosition) return 'sidebar-right'
    
    const lower = rawPosition.toLowerCase().trim()
    // If exact match with allowed position, return it
    if (allowedPositions.includes(lower as Banner['position'])) {
      return lower as Banner['position']
    }
    // If mapped, return mapped
    if (positionMap[lower]) {
      return positionMap[lower]
    }
    // Default fallback
    return 'sidebar-right'
  }

  const rawPosition = data.position
  const normalizedPosition = normalizePosition(rawPosition)

  return {
    id: docId,
    title: data.title || '',
    description: data.description || '',
    imageUrl: data.imageUrl || '',
    link: data.link || '',
    altText: data.altText || '',
    position: normalizedPosition,
    // Se o campo 'active' não existir, assumir true (banner ativo por padrão)
    // Isso resolve problemas de banners importados sem o campo explícito
    active: 'active' in data ? data.active : true,
    startDate: data.startDate || '',
    endDate: data.endDate || '',
    priority: data.priority || 1,
    clickCount: data.clickCount || 0,
    impressionCount: data.impressionCount || 0,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    createdBy: data.createdBy || '',
  }
}

// Helper function to convert Banner to Firestore data
const bannerToFirestore = (banner: Omit<Banner, 'id' | 'createdAt' | 'updatedAt'>) => {
  return {
    title: banner.title,
    description: banner.description,
    imageUrl: banner.imageUrl,
    link: banner.link,
    altText: banner.altText,
    position: banner.position,
    active: banner.active,
    startDate: banner.startDate,
    endDate: banner.endDate,
    priority: banner.priority,
    clickCount: banner.clickCount,
    impressionCount: banner.impressionCount,
    createdBy: banner.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
}

// Mock data generation
const generateMockBanners = (): Banner[] => {
  return [
    {
      id: 'banner-1',
      title: 'Promoção de Imóveis',
      description: 'Ofertas especiais para brasileiros em Miami',
      imageUrl: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
      link: '/listings?category=property',
      altText: 'Imóveis em Miami',
      position: 'sidebar-right',
      active: true,
      startDate: '2026-04-01',
      endDate: '2026-04-30',
      priority: 8,
      clickCount: 42,
      impressionCount: 1250,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
      createdBy: 'admin-001'
    },
    {
      id: 'banner-2',
      title: 'Carros com Desconto',
      description: 'Financiamento facilitado para veículos',
      imageUrl: 'https://images.unsplash.com/photo-1553440569-bcc63803a83d?w=800',
      link: '/listings?category=car',
      altText: 'Carros à venda',
      position: 'sidebar-left',
      active: true,
      startDate: '2026-04-01',
      endDate: '2026-05-31',
      priority: 7,
      clickCount: 38,
      impressionCount: 980,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
      createdBy: 'admin-001'
    },
    {
      id: 'banner-3',
      title: 'Workshop de Imigração',
      description: 'Evento gratuito sobre vistos de trabalho',
      imageUrl: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800',
      link: '/news',
      altText: 'Workshop de imigração',
      position: 'header-top',
      active: true,
      startDate: '2026-04-03',
      endDate: '2026-04-10',
      priority: 9,
      clickCount: 15,
      impressionCount: 520,
      createdAt: new Date('2026-04-02'),
      updatedAt: new Date('2026-04-02'),
      createdBy: 'admin-002'
    },
    {
      id: 'banner-4',
      title: 'Serviços Profissionais',
      description: 'Encontre prestadores de serviço confiáveis',
      imageUrl: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800',
      link: '/listings?category=service',
      altText: 'Serviços profissionais',
      position: 'footer-bottom',
      active: true,
      startDate: '2026-04-01',
      endDate: '2026-04-30',
      priority: 5,
      clickCount: 27,
      impressionCount: 760,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
      createdBy: 'admin-001'
    },
    {
      id: 'banner-5',
      title: 'Oferta Especial de Primavera',
      description: 'Descontos exclusivos em imóveis selecionados',
      imageUrl: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=800',
      link: '/listings?category=property&special=true',
      altText: 'Oferta de primavera',
      position: 'between-sections',
      active: true,
      startDate: '2026-04-01',
      endDate: '2026-04-30',
      priority: 6,
      clickCount: 31,
      impressionCount: 890,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
      createdBy: 'admin-001'
    },
    {
      id: 'banner-6',
      title: 'Baixe Nosso App!',
      description: 'Tenha o Noé Connect sempre à mão. Instale agora!',
      imageUrl: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800',
      link: '/install-app',
      altText: 'Instalar aplicativo',
      position: 'popup',
      active: true,
      startDate: '2026-04-01',
      endDate: '2026-04-30',
      priority: 10,
      clickCount: 56,
      impressionCount: 2100,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
      createdBy: 'admin-001'
    },
  ]
}

// Service functions
export const bannerService = {
  // Get all banners with optional filters
  async getBanners(filters: BannerFilters = {}): Promise<Banner[]> {
    logger.log('getBanners called, isMock:', isMock, 'db available:', !!db, 'filters:', filters)
    if (isMock) {
      const mockBanners = generateMockBanners()
       // Simple filtering (for demo)
      const filtered = mockBanners.filter(banner => {
        if (filters.position && banner.position !== filters.position) return false
        if (filters.active !== undefined && banner.active !== filters.active) return false
        if (filters.search && !banner.title.toLowerCase().includes(filters.search.toLowerCase())) return false
        if (filters.startDate && banner.startDate && banner.startDate < filters.startDate) return false
        if (filters.endDate && banner.endDate && banner.endDate > filters.endDate) return false
        return true
      })
      logger.log('Mock banners filtered:', { position: filters.position, active: filters.active, total: mockBanners.length, filtered: filtered.length })
      return filtered
    }

    try {
      const bannersRef = collection(db, BANNERS_COLLECTION)
      logger.log('Firestore collection ref created:', BANNERS_COLLECTION)
      
      // Buscar todos os banners sem filtros complexos para evitar necessidade de índices
      const q = query(bannersRef)
      const querySnapshot = await getDocs(q)
      logger.log('Firestore query snapshot size:', querySnapshot.size)
      const banners: Banner[] = []

      querySnapshot.forEach((doc) => {
        const data = doc.data()
        logger.log('Processing banner doc:', doc.id, { 
          title: data.title,
          position: data.position,
          active: data.active,
          hasPositionField: 'position' in data,
          hasActiveField: 'active' in data
        })
        const banner = firestoreToBanner(doc.id, data)
        banners.push(banner)
      })
      logger.log('All banners after conversion:', banners.map(b => ({ id: b.id, title: b.title, position: b.position, active: b.active })))

      // Aplicar filtros e ordenação no cliente
      let filteredBanners = banners
      
      logger.log('Before filtering - total banners:', banners.length)
      logger.log('Filters applied:', { position: filters.position, active: filters.active })
      
      // Log all available positions for debugging
      const allPositions = [...new Set(banners.map(b => b.position))]
      logger.log('Available banner positions:', allPositions)
      logger.log('Available active states:', [...new Set(banners.map(b => b.active))])
      
      if (filters.position) {
        const before = filteredBanners.length
        filteredBanners = filteredBanners.filter(banner => {
          const matches = banner.position === filters.position
          if (!matches) {
            logger.log(`Banner "${banner.title}" filtered out - position: ${banner.position}, expected: ${filters.position}`)
          }
          return matches
        })
        logger.log(`After position filter (${filters.position}): ${before} → ${filteredBanners.length}`)
      }
      if (filters.active !== undefined) {
        const before = filteredBanners.length
        filteredBanners = filteredBanners.filter(banner => {
          const matches = banner.active === filters.active
          if (!matches) {
            logger.log(`Banner "${banner.title}" filtered out - active: ${banner.active}, expected: ${filters.active}`)
          }
          return matches
        })
        logger.log(`After active filter (${filters.active}): ${before} → ${filteredBanners.length}`)
      }
      if (filters.search) {
        filteredBanners = filteredBanners.filter(banner => 
          banner.title.toLowerCase().includes(filters.search!.toLowerCase())
        )
      }
      if (filters.startDate) {
        filteredBanners = filteredBanners.filter(banner => 
          !banner.startDate || banner.startDate >= filters.startDate!
        )
      }
      if (filters.endDate) {
        filteredBanners = filteredBanners.filter(banner => 
          !banner.endDate || banner.endDate <= filters.endDate!
        )
      }
      
      // Ordenar por prioridade (descendente) e createdAt (mais recente primeiro)
      filteredBanners.sort((a, b) => {
        if (b.priority !== a.priority) {
          return b.priority - a.priority
        }
        return b.createdAt.getTime() - a.createdAt.getTime()
      })

      logger.log('Returning filtered banners:', filteredBanners.length)
      
      // Fallback to mock data if no banners found in Firestore (for development/demo)
      if (filteredBanners.length === 0 && filters.position && !isMock) {
        logger.log('No banners found for position, falling back to mock data for position:', filters.position)
        const mockBanners = generateMockBanners()
        const mockFiltered = mockBanners.filter(banner => {
          if (filters.position && banner.position !== filters.position) return false
          if (filters.active !== undefined && banner.active !== filters.active) return false
          return true
        })
        // Apply same sorting
        mockFiltered.sort((a, b) => {
          if (b.priority !== a.priority) return b.priority - a.priority
          return b.createdAt.getTime() - a.createdAt.getTime()
        })
        return mockFiltered
      }
      
      return filteredBanners
    } catch (error) {
      logger.error('Error fetching banners from Firestore:', error)
      
      // Check if it's a permission error
      const errorMessage = String(error).toLowerCase()
      const isPermissionError = errorMessage.includes('permission') || 
                               errorMessage.includes('missing or insufficient') ||
                               (error && typeof error === 'object' && 'code' in error && 
                                (error.code === 'permission-denied' || error.code === 'PERMISSION_DENIED'))
      
      if (isPermissionError) {
        logger.warn('Firestore permission denied for banners. Using mock data as fallback.')
        logger.warn('To fix this permanently, update Firestore rules to allow public read access to "banners" collection.')
        logger.warn('In Firebase Console: Firestore → Rules → Update to: allow read: if true;')
        
        // Return mock banners with filters applied
        const mockBanners = generateMockBanners()
        let filtered = mockBanners
        
        if (filters.position) {
          filtered = filtered.filter(banner => banner.position === filters.position)
        }
        if (filters.active !== undefined) {
          filtered = filtered.filter(banner => banner.active === filters.active)
        }
        if (filters.search) {
          filtered = filtered.filter(banner => 
            banner.title.toLowerCase().includes(filters.search!.toLowerCase())
          )
        }
        if (filters.startDate) {
          filtered = filtered.filter(banner => 
            !banner.startDate || banner.startDate >= filters.startDate!
          )
        }
        if (filters.endDate) {
          filtered = filtered.filter(banner => 
            !banner.endDate || banner.endDate <= filters.endDate!
          )
        }
        
        // Apply sorting
        filtered.sort((a, b) => {
          if (b.priority !== a.priority) return b.priority - a.priority
          return b.createdAt.getTime() - a.createdAt.getTime()
        })
        
        return filtered
      }
      
      return []
    }
  },

  // Get single banner by ID
  async getBannerById(id: string): Promise<Banner | null> {
    if (isMock) {
      const mockBanners = generateMockBanners()
      return mockBanners.find(banner => banner.id === id) || null
    }

    try {
      const bannerRef = doc(db, BANNERS_COLLECTION, id)
      const bannerDoc = await getDoc(bannerRef)
      
      if (!bannerDoc.exists()) {
        return null
      }
      
      return firestoreToBanner(bannerDoc.id, bannerDoc.data())
    } catch (error) {
      logger.error('Error fetching banner from Firestore:', error)
      
      // Check if it's a permission error
      const errorMessage = String(error).toLowerCase()
      const isPermissionError = errorMessage.includes('permission') || 
                               errorMessage.includes('missing or insufficient') ||
                               (error && typeof error === 'object' && 'code' in error && 
                                (error.code === 'permission-denied' || error.code === 'PERMISSION_DENIED'))
      
      if (isPermissionError) {
        logger.warn('Firestore permission denied for single banner. Using mock data as fallback.')
        const mockBanners = generateMockBanners()
        return mockBanners.find(banner => banner.id === id) || null
      }
      
      return null
    }
  },

  // Create new banner
  async createBanner(banner: Omit<Banner, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    logger.log('createBanner called, isMock:', isMock, 'db available:', !!db, 'banner:', banner)
    if (isMock) {
      logger.log('Mock: creating banner', banner)
      return 'mock-banner-id-' + Date.now()
    }

    try {
      const firestoreData = bannerToFirestore(banner)
      logger.log('Firestore data:', firestoreData)
      const bannersRef = collection(db, BANNERS_COLLECTION)
      logger.log('Adding document to collection:', BANNERS_COLLECTION)
      const docRef = await addDoc(bannersRef, firestoreData)
      logger.log('Document created with ID:', docRef.id)
      return docRef.id
    } catch (error) {
      logger.error('Error creating banner in Firestore:', error)
      throw error
    }
  },

  // Update existing banner
  async updateBanner(id: string, updates: Partial<Banner>): Promise<void> {
    if (isMock) {
      logger.log('Mock: updating banner', id, updates)
      return
    }

    try {
      const bannerRef = doc(db, BANNERS_COLLECTION, id)
      const updateData: DocumentData = {
        ...updates,
        updatedAt: serverTimestamp(),
      }
      // Remove fields that shouldn't be updated
      delete updateData.id
      delete updateData.createdAt
      delete updateData.createdBy
      
      await updateDoc(bannerRef, updateData)
    } catch (error) {
      logger.error('Error updating banner in Firestore:', error)
      throw error
    }
  },

  // Delete banner
  async deleteBanner(id: string): Promise<void> {
    if (isMock) {
      logger.log('Mock: deleting banner', id)
      return
    }

    try {
      const bannerRef = doc(db, BANNERS_COLLECTION, id)
      await deleteDoc(bannerRef)
    } catch (error) {
      logger.error('Error deleting banner from Firestore:', error)
      throw error
    }
  },

  // Upload image to Firebase Storage
  async uploadImage(file: File): Promise<string> {
    logger.log('uploadImage called, isMock:', isMock, 'storage available:', !!storage, 'file:', file.name, file.size, file.type)
    
    // Optimize image before upload (for both real and mock mode to test)
    let optimizedFile = file
    try {
      if (file.type.startsWith('image/')) {
        logger.log('Optimizing image before upload...')
        optimizedFile = await optimizeImage(file)
        logger.log('Image optimized:', {
          originalSize: file.size,
          optimizedSize: optimizedFile.size,
          compression: `${Math.round((1 - (optimizedFile.size / file.size)) * 100)}%`,
          originalType: file.type,
          optimizedType: optimizedFile.type
        })
      }
    } catch (optimizationError) {
      logger.warn('Failed to optimize image, using original:', optimizationError)
      // Continue with original file if optimization fails
    }
    
    // Verificar se estamos em localhost e forçar mock para evitar CORS
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || 
       window.location.hostname === '127.0.0.1')
    
    logger.log('Is localhost:', isLocalhost, 'window location:', window.location?.hostname)
    
    // Verificar se storage é um objeto mock vazio (quando não inicializado)
    const isStorageMock = !storage || Object.keys(storage).length === 0
    
    logger.log('Storage check:', {
      hasStorage: !!storage,
      storageKeys: storage ? Object.keys(storage).length : 0,
      isStorageMock,
      isMock,
      isLocalhost
    })
    
    // Se estamos em localhost, storage é mock ou não disponível, usar imagem mock imediatamente
    // Isso evita tentativas de upload que serão bloqueadas por CORS
    if (isMock || isStorageMock || isLocalhost) {
      logger.log('Using mock image due to:', {
        isMock,
        isStorageMock,
        isLocalhost,
        reason: isLocalhost ? 'localhost detected (CORS issues)' : 
               isStorageMock ? 'storage is mock/empty object' : 
               'mock mode enabled'
      })
      // Return a mock image URL for demo
      return `https://via.placeholder.com/800x600/bfa757/ffffff?text=Noé+Connect+Demo`
    }

    try {
      // Create a unique filename - use optimized file
      const timestamp = Date.now()
      const extension = optimizedFile.name.split('.').pop() || 'webp'
      const filename = `${timestamp}.${extension}`
      logger.log('Creating storage ref for:', `${IMAGES_PATH}/${filename}`)
      
      // Create storage reference
      const storageRef = ref(storage, `${IMAGES_PATH}/${filename}`)
      
      logger.log('Starting upload...')
      
      // Upload file with timeout to avoid hanging on network errors
      const uploadPromise = uploadBytes(storageRef, optimizedFile)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Upload timeout - network or CORS issue')), 15000)
      })
      
      const snapshot = await Promise.race([uploadPromise, timeoutPromise]) as UploadResult
      
      logger.log('Upload completed, snapshot:', snapshot)
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref)
      logger.log('Download URL:', downloadURL)
      
      return downloadURL
    } catch (error) {
      logger.error('Error uploading image to Firebase Storage:', error)
      
      // Log detalhado para debug
      logger.log('Error type:', typeof error, 'Error constructor:', error?.constructor?.name)
      logger.log('Error message:', error && typeof error === 'object' && 'message' in error ? (error as Error).message : error)
      logger.log('Error stack:', error && typeof error === 'object' && 'stack' in error ? (error as Error).stack : undefined)
      
      // Se for erro de CORS, usar imagem mock como fallback
      const errorMessage = String(error).toLowerCase()
      const isCorsError = errorMessage.includes('cors') || 
                         errorMessage.includes('network') ||
                         errorMessage.includes('preflight') ||
                         errorMessage.includes('failed to fetch') ||
                         errorMessage.includes('err_failed') ||
                         errorMessage.includes('blocked by cors') ||
                         errorMessage.includes('upload timeout') ||
                         errorMessage.includes('access-control') ||
                         (error instanceof Error && error.message.toLowerCase().includes('cors'))
      
      if (isCorsError) {
        logger.warn('CORS/network error detected, using mock image as fallback')
        logger.warn('Para habilitar uploads reais, configure CORS no Firebase Storage:')
        logger.warn('1. Acesse https://console.firebase.google.com/')
        logger.warn('2. Vá para Storage → Regras')
        logger.warn('3. Adicione regras para permitir uploads (veja SOLUCAO_CORS.md)')
        logger.warn('4. Configure CORS via gsutil (veja SOLUCAO_CORS.md)')
        logger.warn('URL do projeto:', window.location?.href)
        return `https://via.placeholder.com/800x600/bfa757/ffffff?text=Noé+Connect+Demo`
      }
      
      throw error
    }
  },

  // Increment click count for a banner
  async incrementClick(bannerId: string): Promise<void> {
    if (isMock) {
      logger.log('Mock: incrementing click count for banner', bannerId)
      return
    }

    try {
      const bannerRef = doc(db, BANNERS_COLLECTION, bannerId)
      const bannerDoc = await getDoc(bannerRef)
      
      if (!bannerDoc.exists()) {
        logger.warn('Banner not found:', bannerId)
        return
      }
      
      const currentClickCount = bannerDoc.data().clickCount || 0
      await updateDoc(bannerRef, {
        clickCount: currentClickCount + 1,
        updatedAt: serverTimestamp()
      })
      logger.log('Click count incremented for banner:', bannerId, 'new count:', currentClickCount + 1)
    } catch (error) {
      logger.error('Error incrementing click count:', error)
      // Don't throw to avoid breaking the user experience
    }
  },

  // Increment impression count for a banner
  async incrementImpression(bannerId: string): Promise<void> {
    if (isMock) {
      logger.log('Mock: incrementing impression count for banner', bannerId)
      return
    }

    try {
      const bannerRef = doc(db, BANNERS_COLLECTION, bannerId)
      const bannerDoc = await getDoc(bannerRef)
      
      if (!bannerDoc.exists()) {
        logger.warn('Banner not found:', bannerId)
        return
      }
      
      const currentImpressionCount = bannerDoc.data().impressionCount || 0
      await updateDoc(bannerRef, {
        impressionCount: currentImpressionCount + 1,
        updatedAt: serverTimestamp()
      })
      logger.log('Impression count incremented for banner:', bannerId, 'new count:', currentImpressionCount + 1)
    } catch (error) {
      logger.error('Error incrementing impression count:', error)
      // Don't throw to avoid breaking the user experience
    }
  },

  // Get all positions
  getPositions(): string[] {
    return ['sidebar-left', 'sidebar-right', 'header-top', 'footer-bottom', 'between-sections', 'popup']
  }
}